import Const from 'const';
import {sortArrayOfObjects} from 'helpers/common-helper';
import CorrelationStackLineupAnalyzer from './correlation-stack-lineup-analyzer';
import {ComboUnit, ComboUnitInterface} from '../interfaces/combo-unit';

export interface CorrelationStackGroups {
    usedInLineups: any[];
    unusedInLineups: any[];
}

export default class CorrelationStackGroupsBuilder {
    private readonly players;
    private readonly comboExpMax;
    private readonly stacking;
    private primaryGroups: object = {};
    private universalGroups: object = {};

    constructor(params) {
        this.players = params.players;
        this.comboExpMax = params.comboExpMax;
        this.stacking = params.stacking;
    }

    public prepareGroups(lineups): CorrelationStackGroups {
        return {
            usedInLineups: this.getGroupsUsedInLineups(lineups),
            unusedInLineups: this.getGroupsUnusedInLineups(),
        };
    }

    private getGroupsUsedInLineups(lineups): any[] {
        lineups.map((lineup) => this.createGroupsUsedInLineup(lineup));

        const groupsUsedInLineups: any[] = [];
        Object.values(this.primaryGroups).map((group) => {
            group.sortedCombos = this.sortCombosByQuantity(group.combos);
            groupsUsedInLineups.push(group);
        });

        Object.values(this.universalGroups).map((group) => {
            group.sortedCombos = this.sortCombosByQuantity(group.combos);
            groupsUsedInLineups.push(group);
        });

        return groupsUsedInLineups;
    }

    private createGroupsUsedInLineup(lineup): void {
        const [primaryGroupName] = lineup.getDistilled(Const.LP_CS_PRIMARY_STACK_GROUP_PREFIX);
        const [keyPlayerId] = lineup.getDistilled(Const.LP_CS_UNIVERSAL_STACK_GROUP_PREFIX);

        if (primaryGroupName) {
            this.createPrimaryGroup({lineup, primaryGroupName});
        } else if (keyPlayerId) {
            this.createUniversalGroup({lineup, keyPlayerId});
        }
    }

    private getGroupsUnusedInLineups(): any[] {
        const groupsUnusedInLineups = {};
        Object.keys(this.comboExpMax.all()).map((groupName) => {
            groupsUnusedInLineups[groupName] = {
                id: groupName,
                quantity: 0,
                combos: this.getCombosOfGroupUnusedInLineups(groupName),
            };
        });

        return Object.values(groupsUnusedInLineups).map((group) => {
            // @ts-ignore
            group.sortedCombos = this.sortCombosByQuantity(group.combos);

            return group;
        });
    }

    private getCombosOfGroupUnusedInLineups(groupName): {[comboId: string]: ComboUnitInterface} {
        const combos = {};
        const stackCombos = this.comboExpMax.getStackCombos(groupName);
        Object.values(stackCombos).map((stackCombo) => {
            // @ts-ignore
            const comboId = stackCombo.getId();

            if (this.isComboIdPresentInPrimaryGroup(groupName, comboId)) {
                return;
            }
            // @ts-ignore
            combos[comboId] = ComboUnit.createPrimary(stackCombo.getCombo());
        });

        return {...combos};
    }

    private isComboIdPresentInPrimaryGroup(groupName, comboId): boolean {
        return this.primaryGroups[groupName] && comboId in this.primaryGroups[groupName].combos;
    }

    private sortCombosByQuantity(combos: {[comboId: string]: ComboUnitInterface}): ComboUnitInterface[] {
        return sortArrayOfObjects(Object.values(combos), 'quantity', 'desc');
    }

    private createPrimaryGroup(params): void {
        const {lineup, primaryGroupName: groupName} = params;

        if (!this.stacking.hasOwnProperty(groupName)) {
            return;
        }

        const stackItem = this.stacking[groupName];

        if (!this.primaryGroups.hasOwnProperty(groupName)) {
            this.primaryGroups[groupName] = {
                id: groupName,
                quantity: 0,
                keyPlayer: this.players.getPlayer(stackItem.keyPlayerId),
                isGameStack: stackItem.isGameStack,
                isUniversalStack: false,
                combos: {},
            };
        }

        this.primaryGroups[groupName].quantity++;

        const analyzer = new CorrelationStackLineupAnalyzer(this.players);
        analyzer.analyze(lineup, {
            teamName: this.primaryGroups[groupName].keyPlayer.team,
            isGameStack: stackItem.isGameStack,
            onlyPositions: ['WR', 'TE', 'RB'],
        });

        const stackedPlayerIds = analyzer.getStackedPlayerIds();

        if (isEmpty(stackedPlayerIds)) {
            return;
        }

        const comboId = this.comboExpMax.doComboId(stackedPlayerIds);

        const comboUnit = this.isComboIdPresentInPrimaryGroup(groupName, comboId)
            ? this.primaryGroups[groupName].combos[comboId]
            : ComboUnit.createPrimary(stackedPlayerIds);

        this.primaryGroups[groupName].combos[comboId] = ComboUnit.createPrimary(comboUnit.getPlayerIds(), [
            ...comboUnit.getLineupIds(),
            lineup.getId(),
        ]);
    }

    private createUniversalGroup(params): void {
        const {lineup, keyPlayerId} = params;

        if (!this.universalGroups.hasOwnProperty(keyPlayerId)) {
            this.universalGroups[keyPlayerId] = {
                id: keyPlayerId,
                quantity: 0,
                keyPlayer: this.players.getPlayer(keyPlayerId),
                isGameStack: false,
                isUniversalStack: true,
                combos: {},
            };
        }

        this.universalGroups[keyPlayerId].quantity++;

        const analyzer = new CorrelationStackLineupAnalyzer(this.players);
        analyzer.analyze(lineup, {
            teamName: this.universalGroups[keyPlayerId].keyPlayer.team,
            isGameStack: false,
            onlyPositions: ['WR', 'TE'],
        });
        const stackedPlayerIds = analyzer.getStackedPlayerIds();

        if (isEmpty(stackedPlayerIds)) {
            return;
        }

        const comboId = this.comboExpMax.doComboId(stackedPlayerIds);

        const comboUnit =
            comboId in this.universalGroups[keyPlayerId].combos
                ? this.universalGroups[keyPlayerId].combos[comboId]
                : ComboUnit.createUniversal(stackedPlayerIds);

        this.universalGroups[keyPlayerId].combos[comboId] = ComboUnit.createUniversal(comboUnit.getPlayerIds(), [
            ...comboUnit.getLineupIds(),
            lineup.getId(),
        ]);
    }
}
